import React, { useState, useCallback, useImperativeHandle } from "react";
import _ from "lodash";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";

import { Modal, Button, EmptyState, ToggleSwitch } from "@toddle-edu/ds-web";
import {
  AcademicHonestyIllustration,
  GoalIllustration,
  fontStyles,
} from "@toddle-edu/ds-theme";
import Loading from "UIComponents/Loading";

import { useI18n } from "Hooks";
import {
  getFieldValue,
  getLearningGoalsDataMemoize,
  getPlannerElementNodesQueryVariables,
  getSubjectsBasedOnUnitSelection,
  getSubjectRecommendedPhaseMap,
  STANDARD_UIDS,
  getAssessmentCommonPreferenceValue,
} from "Assessments/modules/Utils";
import {
  getFieldListFromCache,
  getAssessmentCourseDetailsFromCache,
  getAssessmentLearningCourseDetailsFromCache,
} from "Assessments/modules/AssessmentGraphqlHelpers";
import { handledMYPRubricDescriptors } from "modules/AssessmentTool/AssessmentToolModule";
import {
  MYP_OBJECTIVE_RUBRIC,
  MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC,
  TODDLE_LIBRARY,
  PLANNER_ELEMENT_NESTED_CHECKLIST,
  ELEMENT_TYPE_PYP_LEARNING_STANDARD,
  ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  ELEMENT_TYPE_DP_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  ELEMENT_TYPE_MYP_OBJECTIVES,
} from "Constants/stringConstants";
import {
  getSelectedCurriculumProgram,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import { ASSESSMENT_TOOLS_OBJ } from "@toddle-edu/common-utils";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import {
  getFieldListQuery,
  getRecommendedPhaseOfSubjectsQuery,
} from "Assessments/modules/AssessmentQuery";
import {
  getPlannerElementSetsOfCurriculumEnhancer,
  getBenchmarkSetOfCurriculumEnhancer,
  getCurriculumStandardsQueryEnhancer,
  getPYPplannerElementNodeOfCurriculumEnhancer,
} from "Assessments/modules/AssessmentQueryEnhancers";
import { getFieldDataQueryVariables } from "./util";

import LearningGoalsStandardsTagModal from "UnitPlans/components/IBcomponents/LearningGoalsStandardsTagModal";
import FieldComponent from "UnitPlans/components/IBcomponents/FieldComponent";
import DiscardLearningGoalsModal from "../DiscardLearningGoalsModal";
import classes from "./LearningGoalsModal.scss";
import {
  ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION,
  ASSESSMENT_COMMON_PREFERENCE,
} from "Constants/permissionConstants";
import { ASSESSMENT_COMMON_PREFERENCE_KEYS } from "Assessments/modules/Constants";
import ACLStore from "lib/aclStore";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";
import {
  getIsNewGradingSetupEnabled,
  getIsStandardsUIRevampInAssignments,
} from "ClassRoom/modules/utils";
import { getSelectedLearningCourseId } from "Teacher/routes/LearningCourse/modules/Utils";
import { getSettingValue } from "Utils/ConfigurationUtils";
import { updateSettings } from "modules/ConfigurationThunks";

const styles = {
  modalBodyStyle: {
    display: "flex",
    flexDirection: "row",
    padding: "0",
  },
  modalBodyStyleWithoutScroll: {
    display: "flex",
    flexDirection: "row",
    padding: "0",
    overflow: "hidden",
  },
  sideNavigationContainerStyle: {
    height: "100%",
    flex: "0 0 auto",
  },
  emptyTitleStyle: {
    ...fontStyles.demiBold,
  },

  fieldContainerStyle: {
    display: "flex",
    flexDirection: "column",
    gap: "0px",
    padding: "24px 24px 24px 32px",
  },
};

const checklistStyles = {
  checkListContainerStyle: {
    padding: "24px",
    flexDirection: "column",
    flexWrap: "unset",
  },
  listItemStyle: {
    flexBasis: "unset",
  },
};

const subFieldStyles = {
  checkListContainerStyle: {
    paddingBottom: "24px",
    flexDirection: "column",
    flexWrap: "unset",
  },
  listItemStyle: {
    flexBasis: "unset",
  },
};

const getModalDimensions = type => {
  switch (type) {
    case "CheckList":
      return { height: "520px", width: "576px" };
    case "TreeView":
      return { height: "772px", width: "1312px" };
    default:
      return { height: "512px", width: "960px" };
  }
};

const getFlattenValue = value => {
  return _.isArray(value) ? value : _.compact(_.flatten(_.values(value)));
};

const getCourseGradeIds = _.memoize(
  courseDetails => {
    return _.map(courseDetails?.grades, grade => grade?.id);
  },
  courseDetails => JSON.stringify(courseDetails)
);

const LearningGoalsModal = props => {
  const {
    fieldKey,
    fieldLabel,
    unitPlanFields,
    updateInputField,
    onCloseLearningGoalsModal,
    assessmentToolId,
    assessmentToolType,
    handledMYPRubricDescriptors,
    isData,
    isLoading,
    assessmentType,
    unitPlanFieldList,
    dataField,
    emptyPopupSubText,
    assessmentFields,
    hasAddRemoveValues,
    testId,
    isPlannerElement,
    type,
    showSearchFilter,
    showSideNavigation,
    showRadioButton,
    showPopupForSubFields,
    plannerElementType,
    curriculumType,
    subFields,
    plannerElements = [],
    nodes = [],
    courseId,
    customRef,
    value: fieldValue,
    shouldConsiderFieldValue,
    plannerElementSubType,
    fieldObjConfigOptions,
    isFromQuestion,
    getLearningGoalsTagCountTooltip,
    shouldUpdateActiveQuestionOnClick,
    course,
    subjectRecommendedPhaseMap,
    learningCourseId,
    showDataGridUI = false,
    updateSettings,
    savedCurrentDataSetsScopePreference,
  } = props;

  const unitPlan = getFieldValue({ fields: assessmentFields, uid: "unitPlan" });

  const plannerElementNodes = !_.isEmpty(plannerElements)
    ? _.get(_.head(plannerElements), "nodes", [])
    : nodes;

  const fieldData = getLearningGoalsDataMemoize({
    fieldKey,
    plannerElementType,
    unitPlanFields,
    assessmentFields,
    subFields,
    curriculumType,
    plannerElementNodes,
    courseId,
    unitPlanFieldList,
    dataField,
    fieldValue,
    shouldConsiderFieldValue,
    plannerElementSubType,
    fieldObjConfigOptions,
    isPlannerElement,
    getLearningGoalsTagCountTooltip,
    shouldUpdateActiveQuestionOnClick,
    subjectRecommendedPhaseMap,
    learningCourseId,
  });

  const { value: selectedValue = [] } = fieldData;

  // DATA_SET_SCOPE is stored as "ALL" or "UNIT"
  const [currentDataSetsScope, setCurrentDataSetsScope] = useState(
    savedCurrentDataSetsScopePreference
  );
  const [localValue, setLocalValue] = useState(_.get(fieldData, "value"));
  const [subFieldsToUpdate, setSubFieldsToUpdate] = useState({});
  const [isDiscardModalVisible, setIsDiscardModalVisible] = useState(false);

  const { t } = useI18n(["common", "classRoom", "studentGoals"]);

  const { height, width } = getModalDimensions(type);
  const modalHeaderStyle = {
    width: `calc(${width} - 100px)`,
  };

  const showToggleSwitch = !_.isEmpty(unitPlan) && isPlannerElement;

  const isUnitScoped = _.isEqual(currentDataSetsScope, "UNIT");

  const totalSelected = _.size(getFlattenValue(localValue));
  const newSelectionsCount =
    totalSelected - _.size(getFlattenValue(selectedValue));

  const showLoader = _.isFunction(getLearningGoalsTagCountTooltip)
    ? _.isNil(
        getLearningGoalsTagCountTooltip(
          selectedValue,
          shouldUpdateActiveQuestionOnClick
        )
      )
      ? isLoading
      : isLoading
    : isLoading && !isData;

  const mode = _.isEmpty(selectedValue) ? "add" : "edit";
  const assessmentTypeTitle =
    assessmentType == "le"
      ? t("common:learning_experience")
      : isFromQuestion
      ? t("common:question")
      : t("common:assessment");

  const handleToggleSwitch = useCallback(() => {
    const newValue = isUnitScoped ? "ALL" : "UNIT";
    setCurrentDataSetsScope(newValue);
    const stored = getSettingValue({
      name: ASSESSMENT_COMMON_PREFERENCE,
      readFromUserAndOrgLevel: true,
    });
    updateSettings({
      settings: [
        {
          name: ASSESSMENT_COMMON_PREFERENCE,
          value: JSON.stringify({
            ...stored,
            [ASSESSMENT_COMMON_PREFERENCE_KEYS.DATA_SET_SCOPE]: newValue,
          }),
        },
      ],
      showToastMsg: false,
    });
  }, [currentDataSetsScope, updateSettings]);

  const emptyStateSecondaryButton = showToggleSwitch ? (
    <Button
      onClick={handleToggleSwitch}
      testId={`${testId}-modal-emptyStateShowAllLearningGoals`}
    >
      {t("common:select_from_all_label", { label: fieldLabel })}
    </Button>
  ) : null;

  const discardModalObj = {
    add: {
      headerTitle: t("classRoom:discard_selected_learning_goals"),
      message: t("classRoom:discard_selected_learning_goals_message", {
        count: totalSelected,
        label: fieldLabel,
        type: assessmentTypeTitle,
      }),
      buttonLabel1: t("common:keep"),
      buttonLabel2: t("common:discard"),
    },
    edit: {
      headerTitle: t("classRoom:discard_changes_to_selected_learning_goals"),
      message: t(
        "classRoom:discard_changes_to_selected_learning_goals_message",
        {
          count: newSelectionsCount,
          label: fieldLabel,
          type: assessmentTypeTitle,
        }
      ),
      buttonLabel1: t("journal:keep_changes"),
      buttonLabel2: t("common:discard_changes"),
    },
  };

  useImperativeHandle(customRef, () => ({
    handleToggleSwitch,
  }));

  const getEmptyStateData = () => {
    if (isUnitScoped) {
      return {
        illustration: AcademicHonestyIllustration,
        title: t("classRoom:no_label_selected_in_this_unit", {
          label: fieldLabel,
        }),
        subtitle: null,
        secondaryButton: showToggleSwitch ? (
          <Button
            onClick={handleToggleSwitch}
            testId={`${testId}-modal-emptyStateShowAllLearningGoals`}
          >
            {t("common:select_from_all_label", { label: fieldLabel })}
          </Button>
        ) : null,
      };
    }
    return {
      illustration: GoalIllustration,
      title: t("classRoom:no_label_to_select_from", {
        label: fieldLabel,
      }),
      subtitle: t("classRoom:no_label_to_select_from_subtext", {
        label: fieldLabel,
      }),
      secondaryButton: null,
    };
  };

  // whenever MYP objectives change, update MYP rubric descriptors accordingly
  const updateMYPRubricDescriptors = () => {
    const isMYPObjectiveRubric = _.includes(
      [MYP_OBJECTIVE_RUBRIC, MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC],
      assessmentToolType
    );

    if (!isMYPObjectiveRubric) return;

    const oldObjectives = getFieldValue({
      fields: assessmentFields,
      uid: fieldKey,
    });

    if (!_.isEmpty(assessmentToolId)) {
      handledMYPRubricDescriptors({
        newObjectives: localValue,
        oldObjectives,
        rubricId: assessmentToolId,
      });
    }
  };

  const handleUpdateSubFieldsValue = () => {
    _.forEach(subFieldsToUpdate, ({ fieldUid, value, nodes }) => {
      const assessmentFieldValue = getFieldValue({
        fields: assessmentFields,
        uid: fieldUid,
      });
      const sortedAssessmentFieldValues = _.sortBy(
        assessmentFieldValue,
        id => id
      );
      const sortedUpdatedValue = _.sortBy(value, id => id);

      // update only if there is a change
      if (!_.isEqual(sortedAssessmentFieldValues, sortedUpdatedValue)) {
        updateInputField({ [fieldUid]: value }, { nodes });
      }
    });
  };

  const _updateInputField = useCallback(
    ({ params }) => {
      const value = _.get(params, fieldKey, []);
      setLocalValue(value);
    },
    [fieldKey]
  );

  const handleUpdateValue = () => {
    const extraParams = {
      nodes:
        _.get(fieldData, "groupByType") === "SUBJECT" &&
        _.get(fieldData, "isSubjectPropInNode", false)
          ? _.get(fieldData, "allNodes", [])
          : _.get(
              fieldData,
              "globalData.nodes",
              _.get(fieldData, "unitSpecificData.nodes", [])
            ),
      rootNodeId: "",
    };

    updateInputField(
      { [fieldKey]: localValue },
      extraParams,
      true,
      hasAddRemoveValues
    );

    if (!_.isEmpty(subFieldsToUpdate)) {
      handleUpdateSubFieldsValue();
    }

    if (
      _.includes(["objectivesMYP", "interdisciplinaryCriteriaMYP"], fieldKey)
    ) {
      updateMYPRubricDescriptors();
    }

    onCloseLearningGoalsModal();
  };

  const handleCancelButtonClick = e => {
    e?.stopPropagation();

    switch (mode) {
      case "add":
        if (totalSelected > 0) {
          setIsDiscardModalVisible(true);
          return;
        }
        onCloseLearningGoalsModal();
        return;
      default: {
        if (newSelectionsCount > 0) {
          setIsDiscardModalVisible(true);
          return;
        }
        onCloseLearningGoalsModal();
      }
    }
  };

  const onCloseDiscardModal = () => {
    setIsDiscardModalVisible(false);
  };

  const renderDiscardModal = () => {
    return (
      <DiscardLearningGoalsModal
        headerTitle={discardModalObj[mode].headerTitle}
        message={discardModalObj[mode].message}
        buttonLabel1={discardModalObj[mode].buttonLabel1}
        buttonLabel2={discardModalObj[mode].buttonLabel2}
        onClickButton1={handleUpdateValue}
        onClickButton2={onCloseLearningGoalsModal}
        onCloseDiscardModal={onCloseDiscardModal}
        testId={testId}
      />
    );
  };

  const renderEmptyState = () => {
    const {
      illustration,
      title,
      subtitle,
      secondaryButton,
    } = getEmptyStateData();

    return (
      <div className={classes.emptyStateContainer}>
        <EmptyState
          illustration={illustration}
          title={title}
          subtitle={subtitle}
          titleStyle={styles.emptyTitleStyle}
          secondaryButton={secondaryButton}
        />
      </div>
    );
  };

  const renderModalHeader = () => {
    return (
      <div className={classes.modalHeader} style={modalHeaderStyle}>
        <div className={classes.modalHeaderTitle}>{fieldLabel}</div>
        {showToggleSwitch ? (
          <div className={classes.toggleButtonContainer}>
            <div className="text-body-1">
              {t("classRoom:only_show_label_selected_in_this_unit", {
                label: fieldLabel,
              })}
            </div>
            <ToggleSwitch
              checked={isUnitScoped}
              onChange={handleToggleSwitch}
              testId={`${testId}-modal-currentDataSetsScopeSelectionToggle`}
            />
          </div>
        ) : null}
      </div>
    );
  };

  const isSubjectStandard = _.includes(
    [
      ELEMENT_TYPE_PYP_LEARNING_STANDARD,
      ELEMENT_TYPE_MYP_LEARNING_STANDARD,
      ELEMENT_TYPE_DP_LEARNING_STANDARD,
      ELEMENT_TYPE_UBD_LEARNING_STANDARD,
    ],
    plannerElementType
  );

  const courseDetails = getIsLearningCoursesEnabled()
    ? getAssessmentLearningCourseDetailsFromCache({
        id: learningCourseId,
      })
    : getAssessmentCourseDetailsFromCache({
        id: courseId,
      });

  const courseGradeIds = getCourseGradeIds(courseDetails);

  const renderModalBody = () => {
    return (
      <FieldComponent
        {...fieldData}
        type={type}
        fieldKey={fieldKey}
        fieldLabel={fieldLabel}
        showSearchFilter={showSearchFilter}
        showSideNavigation={showSideNavigation}
        showRadioButton={showRadioButton}
        showPopupForSubFields={showPopupForSubFields}
        showAllLearningGoals={!isUnitScoped}
        isPlannerElement={isPlannerElement}
        value={localValue}
        updateInputField={_updateInputField}
        setSubFieldsToUpdate={setSubFieldsToUpdate}
        dataField={dataField}
        unitPlanFields={unitPlanFields}
        assessmentFields={assessmentFields}
        unitPlanFieldList={unitPlanFieldList}
        emptyStateSecondaryButton={emptyStateSecondaryButton}
        testId={`${testId}-modal`}
        styles={checklistStyles}
        subFieldStyles={subFieldStyles}
        fieldContainerStyle={styles.fieldContainerStyle}
        emptyStateComponent={renderEmptyState()}
        emptyPopupSubText={emptyPopupSubText}
        getLearningGoalsTagCountTooltip={getLearningGoalsTagCountTooltip}
        showGradesFilter={isSubjectStandard}
        courseGradeIds={courseGradeIds}
        shouldUpdateActiveQuestionOnClick={shouldUpdateActiveQuestionOnClick}
        courseDetails={course}
        plannerElementType={plannerElementType}
        showDataGridUI={showDataGridUI}
      />
    );
  };

  const renderModalFooter = () => {
    const selectButtonDisabled = mode === "add" && totalSelected == 0;

    return (
      <div className={classes.modalFooter}>
        <div className="heading-6">
          {t("common:count_selected", { count: totalSelected })}
        </div>
        <div className={classes.buttonContainer}>
          <Button
            _legacy_variant={"outlined-subtle"}
            variant="neutral"
            type="outlined"
            onClick={handleCancelButtonClick}
            testId={`${testId}-modal-cancel`}
          >
            {t("common:cancel")}
          </Button>
          <Button
            variant={"progressive"}
            onClick={handleUpdateValue}
            disabled={selectButtonDisabled}
            testId={`${testId}-modal-select`}
          >
            {t("common:select")}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Modal
      height={height}
      width={width}
      hasOverlay={true}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={false}
      onClose={handleCancelButtonClick}
    >
      <Modal.Header testId={testId} hasSeparator={true} isClosable={true}>
        {renderModalHeader()}
      </Modal.Header>
      <Modal.Body
        isScrollable={!showDataGridUI}
        style={
          showDataGridUI
            ? styles.modalBodyStyleWithoutScroll
            : styles.modalBodyStyle
        }
      >
        {showLoader ? <Loading /> : renderModalBody()}
      </Modal.Body>
      <Modal.Footer hasSeparator={true}>{renderModalFooter()}</Modal.Footer>
      {isDiscardModalVisible ? renderDiscardModal() : null}
    </Modal>
  );
};

const mapActionCreators = {
  handledMYPRubricDescriptors,
  updateSettings,
};

const mapStateToProps = (state, ownProps) => {
  const {
    isPlannerElement,
    assessmentFields,
    selectedCourseId,
    unitPlanFields,
    plannerElementType,
    plannerElementSubType,
    assessmentTools,
    fieldKey,
    curriculumProgram: assessmentCurriculumProgram,
    learningCourseId,
  } = ownProps;

  const isToddleLibrary = state.login.activeTab == TODDLE_LIBRARY;

  const curriculumProgram = isToddleLibrary
    ? _.get(state, "classRoom.curriculumProgram")
    : getSelectedCurriculumProgram(state) || assessmentCurriculumProgram;

  const academicYearId = getSelectedAcademicYearId(state);

  const courseId =
    selectedCourseId || _.get(state, "teacher.selected_class.selected_course");

  const courseGradeFilters = {
    academicYearId,
  };
  const course = getCourseDetailsFromCache({
    id: courseId,
    courseGradeFilters,
  });

  const { node = {} } =
    _.find(assessmentTools?.edges, ({ node }) => {
      const assessmentToolType = _.get(node, "assessmentToolType", null);

      return (
        fieldKey ===
        _.get(
          ASSESSMENT_TOOLS_OBJ[assessmentToolType],
          "objectiveFieldKey",
          null
        )
      );
    }) || {};

  const assessmentToolId = _.get(node, "assessmentToolItem.id");
  const assessmentToolType = _.get(node, "assessmentToolType");
  const curriculumNodeInterfaceType = _.get(
    curriculumProgram,
    "nodeInterfaceType",
    ""
  );
  const fieldDataQueryVariables = getFieldDataQueryVariables({
    curriculumProgramId: _.get(curriculumProgram, "id"),
    curriculumNodeInterfaceType,
    fieldKey,
  });

  const assessmentSubjects = getSubjectsBasedOnUnitSelection({
    assessmentFields,
    unitPlanFields,
    courseId,
    curriculumType: _.get(curriculumProgram, "type"),
    learningCourseId,
  });

  const assessmentSubjectIds = _.map(assessmentSubjects, "id");

  const isPhaseRecommendationEnabled = ACLStore.can(
    ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION
  );

  const savedCurrentDataSetsScopePreference = getAssessmentCommonPreferenceValue(
    ASSESSMENT_COMMON_PREFERENCE_KEYS.DATA_SET_SCOPE
  );

  return {
    assessmentToolId,
    assessmentToolType,
    isData: true,
    isLoading: false,
    fieldDataQueryVariables,
    ...getPlannerElementNodesQueryVariables({
      plannerElementType,
      assessmentFields,
      unitPlanFields,
      courseId,
      curriculumProgram,
      isStepWithPlannerElement: isPlannerElement,
      skipQuery: false,
      course,
      plannerElementSubType,
      learningCourseId,
    }),
    courseId,
    plannerElementSets: _.get(state, "platform.currentPlannerElementSets", []),
    course,
    assessmentSubjectIds,
    isPhaseRecommendationEnabled,
    savedCurrentDataSetsScopePreference,
  };
};

const LearningGoalsModalWithQuery = compose(
  connect(mapStateToProps, mapActionCreators),
  getPlannerElementSetsOfCurriculumEnhancer,
  getPYPplannerElementNodeOfCurriculumEnhancer,
  getBenchmarkSetOfCurriculumEnhancer,
  getCurriculumStandardsQueryEnhancer,
  // Fetch planner element set in case of NESTED CHECKLIST.
  graphql(getFieldListQuery, {
    name: "getFieldList",
    skip: ({ plannerElementType, skipQuery }) =>
      plannerElementType !== PLANNER_ELEMENT_NESTED_CHECKLIST || !!skipQuery,
    options: ({ fieldDataQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: fieldDataQueryVariables,
    }),
    props({
      getFieldList,
      ownProps: { isLoading, isData, fieldKey, fieldDataQueryVariables },
    }) {
      const data = getFieldListFromCache(fieldDataQueryVariables);

      const plannerElements = [
        _.get(
          _.find(_.get(data, "templateField.edges", []), {
            node: { uid: fieldKey },
          }),
          "node.plannerElementSet"
        ),
      ];

      return {
        plannerElements,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          getFieldList["networkStatus"] == 1 ||
          getFieldList["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  graphql(getRecommendedPhaseOfSubjectsQuery, {
    name: "getRecommendedPhaseOfSubjects",
    skip: ({
      plannerElementType,
      isPhaseRecommendationEnabled,
      assessmentSubjectIds,
    }) =>
      !_.isEqual(plannerElementType, ELEMENT_TYPE_MYP_OBJECTIVES) ||
      !isPhaseRecommendationEnabled ||
      _.isEmpty(assessmentSubjectIds),
    options: ({ assessmentSubjectIds }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ids: assessmentSubjectIds,
      },
    }),
    props({ getRecommendedPhaseOfSubjects, ownProps: { isData, isLoading } }) {
      const data = getRecommendedPhaseOfSubjects.multiNode;
      const subjectRecommendedPhaseMap = getSubjectRecommendedPhaseMap({
        subjects: data,
      });

      return {
        subjectRecommendedPhaseMap,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes([1, 2], getRecommendedPhaseOfSubjects["networkStatus"]) ||
          isLoading,
      };
    },
  })
)(LearningGoalsModal);

const LearningGoalsModalWrapper = props => {
  const { isStandardField } = props;

  if (getIsNewGradingSetupEnabled() && isStandardField) {
    return <LearningGoalsStandardsTagModal {...props} />;
  }

  return (
    <LearningGoalsModalWithQuery
      {...props}
      showDataGridUI={getIsStandardsUIRevampInAssignments() && isStandardField}
    />
  );
};

const mapStateToPropsForWrapper = (state, ownProps) => {
  const { fieldKey, curriculumProgram: assessmentCurriculumProgram } = ownProps;

  const isToddleLibrary = state?.login?.activeTab == TODDLE_LIBRARY;

  // * Getting the curriculum program from the state or props
  const curriculumProgram = isToddleLibrary
    ? _.get(state, "classRoom.curriculumProgram")
    : assessmentCurriculumProgram
    ? assessmentCurriculumProgram
    : getSelectedCurriculumProgram(state);

  // * Getting the curriculum program type to determine if the field is a standard field
  const curriculumProgramType = _.get(curriculumProgram, "type");
  const standardUID = STANDARD_UIDS[curriculumProgramType];
  const isStandardField = fieldKey === standardUID;

  const learningCourseId = getSelectedLearningCourseId(state);

  return { isStandardField, learningCourseId };
};

export default connect(
  mapStateToPropsForWrapper,
  null
)(LearningGoalsModalWrapper);
